/**
 * TitleDraftManager handles draft storage for new quests/posts (title drafts).
 * This is separate from the reply draft system which uses quest-specific keys.
 */
class TitleDraftManager {
    static STORAGE_KEY = 'title-quest-drafts'
    static ANSWER_DRAFT_STORAGE_KEY_PREFIX = 'draft-answer-'
    static MAX_DRAFTS = 20
    static MIN_CONTENT_LENGTH = 1 // Lower threshold to save drafts earlier

    static sortDrafts(drafts) {
        return drafts.sort((a, b) => {
            // Starred drafts come first
            if (a.starred && !b.starred) return -1
            if (!a.starred && b.starred) return 1
            // Then sort by timestamp
            return (b.timestamp || 0) - (a.timestamp || 0)
        })
    }

    static limitDrafts(drafts) {
        const sortedDrafts = this.sortDrafts(drafts)
        if (sortedDrafts.length <= this.MAX_DRAFTS) return sortedDrafts

        const starredDrafts = sortedDrafts.filter(d => d.starred)
        const unstarredDrafts = sortedDrafts.filter(d => !d.starred)
        // Keep all starred drafts plus as many unstarred as will fit
        const maxUnstarred = Math.max(0, this.MAX_DRAFTS - starredDrafts.length)
        return [...starredDrafts, ...unstarredDrafts.slice(0, maxUnstarred)]
    }

    static isQuotaExceededError(error) {
        return (
            error?.name === 'QuotaExceededError' ||
            error?.name === 'NS_ERROR_DOM_QUOTA_REACHED' ||
            error?.code === 22 ||
            error?.code === 1014
        )
    }

    static writeDrafts(drafts) {
        if (drafts.length === 0) {
            localStorage.removeItem(this.STORAGE_KEY)
            return
        }

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(drafts))
    }

    static withoutLeastRecentlyUsedDraft(drafts, currentDraftId) {
        if (drafts.length === 0) return drafts

        const indexedDrafts = drafts.map((draft, index) => ({ draft, index }))
        const nonCurrentDrafts = indexedDrafts.filter(({ draft }) => draft.id !== currentDraftId)
        const removableDrafts = nonCurrentDrafts.length > 0 ? nonCurrentDrafts : indexedDrafts
        const unstarredDrafts = removableDrafts.filter(({ draft }) => !draft.starred)
        const candidates = unstarredDrafts.length > 0 ? unstarredDrafts : removableDrafts
        const leastRecentlyUsed = candidates.reduce((oldest, candidate) =>
            (candidate.draft.timestamp || 0) < (oldest.draft.timestamp || 0) ? candidate : oldest,
        )

        return drafts.filter((_, index) => index !== leastRecentlyUsed.index)
    }

    static getDraftStorageKeys() {
        const keys = []

        try {
            for (let index = 0; index < localStorage.length; index += 1) {
                const key = localStorage.key(index)
                if (key?.startsWith(this.ANSWER_DRAFT_STORAGE_KEY_PREFIX)) {
                    keys.push(key)
                }
            }
        } catch (error) {
            return []
        }

        return keys
    }

    static getDraftStorageKeyTimestamp(key) {
        try {
            const value = localStorage.getItem(key)
            const parsed = value ? JSON.parse(value) : null
            return typeof parsed?.timestamp === 'number' ? parsed.timestamp : 0
        } catch (error) {
            return 0
        }
    }

    static removeLeastRecentlyUsedDraftStorageKey() {
        const key = this.getDraftStorageKeys().sort(
            (a, b) => this.getDraftStorageKeyTimestamp(a) - this.getDraftStorageKeyTimestamp(b),
        )[0]

        if (!key) return false

        try {
            localStorage.removeItem(key)
            return true
        } catch (error) {
            console.error('Error removing old draft key:', error)
            return false
        }
    }

    static saveDraftsWithQuotaRecovery(drafts, currentDraftId) {
        let nextDrafts = this.limitDrafts(drafts)

        try {
            this.writeDrafts(nextDrafts)
            return nextDrafts
        } catch (error) {
            console.error('Error saving draft:', error)
            if (!this.isQuotaExceededError(error)) return nextDrafts
        }

        while (this.removeLeastRecentlyUsedDraftStorageKey()) {
            try {
                this.writeDrafts(nextDrafts)
                return nextDrafts
            } catch (error) {
                if (!this.isQuotaExceededError(error)) {
                    console.error('Error saving drafts after removing old draft key:', error)
                    return nextDrafts
                }
            }
        }

        while (nextDrafts.length > 0) {
            nextDrafts = this.withoutLeastRecentlyUsedDraft(nextDrafts, currentDraftId)

            try {
                this.writeDrafts(nextDrafts)
                return nextDrafts
            } catch (error) {
                if (!this.isQuotaExceededError(error)) {
                    console.error('Error saving pruned drafts:', error)
                    return nextDrafts
                }
            }
        }

        try {
            this.writeDrafts([])
        } catch (error) {
            console.error('Error clearing drafts after quota exceeded:', error)
        }

        return []
    }

    /**
     * Load all drafts from localStorage
     * @returns {Array} Array of draft objects sorted by starred status, then timestamp (newest first)
     */
    static load() {
        try {
            const drafts = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]')
            if (!Array.isArray(drafts)) return []
            return this.sortDrafts(drafts)
        } catch (error) {
            console.error('Error loading drafts:', error)
            return []
        }
    }

    /**
     * Save or update a draft with a specific ID
     * @param {string} draftId - UUID of the draft
     * @param {string} content - Plain text content
     * @param {object} deltaJson - Quill delta format
     */
    static saveWithId(draftId, content, deltaJson, options = {}) {
        const contentText = content || ''
        const hasPollDraft =
            options.message_type === 'poll' &&
            Array.isArray(options.poll_options) &&
            options.poll_options.some(option => option?.trim?.().length > 0)
        const actionRequests = Array.isArray(options.action_requests) ? options.action_requests : []
        const hasActionRequestDraft = actionRequests.length > 0
        const hasPayloadDraft = options.payloads && Object.keys(options.payloads).length > 0

        if (
            contentText.trim().length < this.MIN_CONTENT_LENGTH &&
            !hasPollDraft &&
            !hasActionRequestDraft &&
            !hasPayloadDraft
        ) {
            // Don't save drafts that are too short
            this.remove(draftId)
            return
        }

        let drafts = this.load()

        // Find existing draft with this ID
        const existingIndex = drafts.findIndex(d => d.id === draftId)
        const existingDraft = existingIndex >= 0 ? drafts[existingIndex] : null

        const draftData = {
            id: draftId,
            content: contentText.substring(0, 200), // Store preview for list display
            fullContent: contentText, // Store full content
            delta_json: deltaJson,
            action_requests: actionRequests,
            payloads: options.payloads || {},
            suppressed_preview_urls: options.suppressed_preview_urls || [],
            message_type: options.message_type || null,
            poll_options: options.poll_options || [],
            poll_duration_minutes: options.poll_duration_minutes || null,
            starred: !!existingDraft?.starred,
            timestamp: Date.now(),
            wordCount: contentText.split(/\s+/).filter(w => w.length > 0).length,
        }

        if (existingIndex >= 0) {
            // Update existing draft
            drafts[existingIndex] = draftData
        } else {
            // New draft - add to beginning
            drafts.unshift(draftData)
        }

        this.saveDraftsWithQuotaRecovery(drafts, draftId)
    }

    /**
     * Toggle starred status of a draft
     * @param {string} draftId - UUID of the draft to star/unstar
     */
    static toggleStar(draftId) {
        const drafts = this.load()
        const draft = drafts.find(d => d.id === draftId)
        if (draft) {
            draft.starred = !draft.starred
            this.saveDraftsWithQuotaRecovery(drafts, draftId)
        }
    }

    /**
     * Remove a specific draft by ID
     * @param {string} draftId - UUID of the draft to remove
     */
    static remove(draftId) {
        const drafts = this.load()
        const filtered = drafts.filter(d => d.id !== draftId)
        this.saveDraftsWithQuotaRecovery(filtered, draftId)
    }

    /**
     * Get count of saved drafts
     * @returns {number} Number of drafts
     */
    static getCount() {
        return this.load().length
    }

    /**
     * Clear all drafts (useful for debugging/testing)
     */
    static clearAll() {
        localStorage.removeItem(this.STORAGE_KEY)
    }

    /**
     * Remove drafts older than specified days
     * @param {number} days - Number of days to keep drafts
     */
    static cleanupOld(days = 30) {
        const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
        const drafts = this.load()
        const filtered = drafts.filter(d => d.timestamp > cutoff)
        this.saveDraftsWithQuotaRecovery(filtered, null)
    }
}

export default TitleDraftManager
